import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useMemo, useState } from "react"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { weekPlan } from "routes/plan/controller/week-plan"
import { PlanId } from "routes/plan/controller/use-plan"
import { withStartAndFinish } from "lib/graphql/start-and-finish"
import { keyBy } from "lib/keyBy"
import { pick } from "lib/pick"
import { useMeasurement } from "lib/@hooks/useMeasurement"
import { Bound } from "lib/@components/binding/Bound"
import { Box, Card, LinearProgress, Stack, Typography } from "@mui/material"
import { PlanDate } from "routes/plan/plan-config/plan-date"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { PlanningWeekView } from "routes/plan/planning-week-view"
import { ListItemBox } from "lib/@components/ListItemBox"
import { RegimeNavigator } from "routes/asset-register/plugins/regime-display/regime-navigator"

export function WeekPlanner() {
    const { parentItem, target } = useBoundContext()
    const [planStart] = useBoundValue("date", new Date().beginningOfDay())
    const [configurations] = useBoundValue("configurations")
    const [year, setYear] = useState(0)
    const [minimumHours] = useBoundValue("minimumHours", 24)
    const [running, setRunning] = useState(false)
    const [search] = useSearchParam("search", "")
    const days = weekPlan
        .useResults(
            {
                prefix: target[PlanId],
                group: parentItem.id,
                startDate: new Date(planStart),
                windowEnd: new Date(planStart).addYears(1 + year),
                windowStart: new Date(planStart).addYears(year),
                configuration: Object.values(configurations),
                minimumIntervalInHours: minimumHours,
                search,
                useWeeks: true,
            },
            withStartAndFinish(
                () => setRunning(true),
                () => setRunning(false)
            )
        )
        ?.days?.sortBy("date")

    const dateIndex = useMemo(() => {
        const result = days ? keyBy(days, (d) => new Date(d.date).short()) : {}
        Object.values(result).forEach((day) => (day.scheduleIndex = keyBy(day.schedules, pick("scheduleId"))))
        return result
    }, [days])

    const [size, attach] = useMeasurement()

    return (
        <Bound
            year={year}
            setYear={setYear}
            planStart={planStart}
            minimumHours={minimumHours}
            parentItem={parentItem}
            days={days}
            dateIndex={dateIndex}
            pageSize={size}
        >
            <Box width={1} ref={attach} pr={0.5}>
                {(!days || running) && (
                    <Stack sx={{ mt: 2 }} spacing={2}>
                        <Typography variant="subtitle2">Calculating Plan...</Typography>
                        <LinearProgress />
                    </Stack>
                )}
                {days && !running && (
                    <>
                        <ListItemBox spacing={1}>
                            <RegimeNavigator sx={{ flexWrap: "wrap", flex: 0, width: "auto", whiteSpace: "nowrap" }} />
                            <SearchBoxUsingSearchParam sx={{ width: 1 }} />
                            <Box>
                                <PlanDate sx={{ m: 0 }} />
                            </Box>
                        </ListItemBox>
                        <Card>
                            <PlanningWeekView date={planStart} />
                        </Card>
                    </>
                )}
            </Box>
        </Bound>
    )
}
